import { useEffect, useRef } from 'react';
import { logger } from 'modules/logger';
import type { PixelDataSharedWorker } from 'modules/viewer/workers/PixelDataSharedWorker';
import type { BaseImagingProvider } from '../ViewportDre/modules/imaging/BaseImagingProvider';
import { WorkerLoadedImagingProvider } from '../ViewportDre/modules/imaging/WorkerLoadedImagingProvider';
import type { Stack } from '../ViewportsConfigurations/types';

/**
 * Self-heals the viewer after the VX-4156 ping/pong watchdog severs and recreates
 * the shared worker connection (VX-4223).
 *
 * The watchdog nulls out the worker so a fresh connection is created, but existing
 * `WorkerLoadedImagingProvider`s still point at the dead port and would otherwise
 * sit on a healthy connection nobody is using — requiring a manual page reload.
 * Recovery only fires when the connection was actually severed: the watchdog
 * nulls the worker out (`oldWorker -> null -> newWorker`), so a legitimate
 * recreation always passes through null. A new worker reference that never
 * passed through null is referential churn (e.g. a re-render handing back a
 * fresh object), not a recovery, and is ignored. The first (initial) worker
 * assignment is likewise ignored, since providers created afterward already use
 * that port.
 */
export function useReinitializeProvidersOnWorkerChange(
  pixelDataSharedWorker: PixelDataSharedWorker | null,
  stackProviders: ReadonlyMap<string, BaseImagingProvider<Stack>>
): void {
  // The worker the existing providers are currently bound to. A ref (not state)
  // so swapping it never triggers a render; the providers are mutated in place.
  const boundWorkerRef = useRef<PixelDataSharedWorker | null>(null);
  // Whether the bound connection was severed (went null) since we last saw it —
  // the signal that distinguishes a watchdog recovery from referential churn.
  const connectionSeveredRef = useRef(false);

  useEffect(() => {
    if (pixelDataSharedWorker == null) {
      if (boundWorkerRef.current != null) connectionSeveredRef.current = true;
      return;
    }

    const previousWorker = boundWorkerRef.current;
    const isRecovery =
      connectionSeveredRef.current &&
      previousWorker != null &&
      previousWorker !== pixelDataSharedWorker;

    boundWorkerRef.current = pixelDataSharedWorker;
    connectionSeveredRef.current = false;

    if (!isRecovery) return;

    // Recovery is the whole point of this hook, so it must be observable and resilient: isolate
    // each reattach so one provider throwing can't strand the rest on the dead port, and log the
    // outcome so the logs show whether the page actually healed (we already log "SW port stale"
    // for the sever, but nothing for the recovery itself).
    let reattachedCount = 0;
    stackProviders.forEach((provider) => {
      if (!(provider instanceof WorkerLoadedImagingProvider)) return;
      try {
        provider.reinitializeWorker(pixelDataSharedWorker);
        reattachedCount += 1;
      } catch (error) {
        logger.error('Failed to reinitialize imaging provider after shared worker reconnect', {
          error,
        });
      }
    });
    logger.info(
      `Reinitialized ${reattachedCount} imaging provider(s) after shared worker reconnect`
    );
  }, [pixelDataSharedWorker, stackProviders]);
}
